import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { createPortal } from 'react-dom';
import { CheckCircle2, Sparkles } from 'lucide-react';
import styles from './SuccessToast.module.css';

interface SuccessToastProps {
  isVisible: boolean;
  onClose: () => void;
  habitName: string;
  message?: string;
  duration?: number;
}

const SuccessToast: React.FC<SuccessToastProps> = ({ 
  isVisible, 
  onClose, 
  habitName, 
  message = 'Hábito concluído!',
  duration = 2000 
}) => {
  const [confetti, setConfetti] = useState<Array<{ id: number; x: number; y: number; rotation: number; color: string }>>([]);

  useEffect(() => {
    if (isVisible) {
      // Tocar som de sucesso
      const playSuccessSound = () => {
        try {
          const audio = new Audio('/sounds/done.mp3');
          audio.volume = 0.35; // 35% do volume
          audio.play().catch(e => console.log('Erro ao tocar som:', e));
        } catch (error) {
          console.log('Erro ao carregar som:', error);
        }
      };

      playSuccessSound();

      // Gerar confetes
      const newConfetti = Array.from({ length: 30 }, (_, i) => ({
        id: i,
        x: Math.random() * 100,
        y: Math.random() * 100,
        rotation: Math.random() * 360,
        color: ['#10B981', '#3B82F6', '#F59E0B', '#EF4444', '#8B5CF6'][Math.floor(Math.random() * 5)]
      }));
      setConfetti(newConfetti);

      // Auto close
      const timer = setTimeout(() => {
        onClose();
      }, duration);

      return () => clearTimeout(timer);
    }
  }, [isVisible, onClose, duration]);

  if (!isVisible) return null;

  return createPortal(
    <div className={styles.toastOverlay}>
      {/* Confetes */}
      <div className={styles.confettiContainer}>
        {confetti.map((piece) => (
          <motion.div
            key={piece.id}
            className={styles.confetti}
            initial={{ 
              x: '50vw', 
              y: '50vh', 
              scale: 0,
              rotate: piece.rotation 
            }}
            animate={{ 
              x: `${piece.x}vw`, 
              y: `${piece.y}vh`, 
              scale: [0, 1, 0],
              rotate: piece.rotation + 180
            }}
            transition={{ 
              duration: 1.5,
              ease: "easeOut"
            }}
            style={{ backgroundColor: piece.color }}
          />
        ))}
      </div>

      {/* Toast principal */}
      <motion.div
        className={styles.toast}
        initial={{ opacity: 0, scale: 0.8, y: 50 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.8, y: -50 }}
        transition={{ duration: 0.3, ease: "easeOut" }}
      >
        <div className={styles.toastContent}>
          <div className={styles.toastIcon}>
            <CheckCircle2 size={24} />
            <motion.div
              className={styles.sparkle}
              animate={{ rotate: 360 }}
              transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
            >
              <Sparkles size={16} />
            </motion.div>
          </div>
          
          <div className={styles.toastText}>
            <h3 className={styles.toastTitle}>{message}</h3>
            <p className={styles.toastSubtitle}>{habitName}</p>
          </div>
        </div>

        <motion.div
          className={styles.progressBar}
          initial={{ width: "100%" }}
          animate={{ width: "0%" }}
          transition={{ duration: duration / 1000, ease: "linear" }}
        />
      </motion.div>
    </div>,
    document.body
  );
};

export default SuccessToast;
